/**
 * Canonical Sticky Add to Bag — premium redesign + merchant-editable controls.
 *
 * No independent cart logic — the sticky button's only job is to safely
 * trigger the ORIGINAL product-form submit button (Dawn's own
 * .product-form__submit, confirmed against snippets/buy-buttons.liquid,
 * inside the main product-info). Dawn's existing variant/quantity/cart-
 * drawer/error/analytics behaviour stays completely authoritative; this
 * file never calls fetch(), never touches routes.cart_add_url, and never
 * dispatches its own cart events. It does not clone forms, duplicate IDs
 * or create another drawer. One activation = one canonical /cart/add, and
 * the existing cart drawer updates itself exactly once via Dawn's events.
 *
 * Responsibilities in this pass:
 *  - Directional cross-viewport geometry. The bar shows on mobile, tablet AND
 *    desktop purely from geometry: it appears only once the canonical buy
 *    button has been scrolled fully ABOVE the top of the viewport (its whole
 *    box satisfies rect.bottom <= 0) — never while the button merely sits
 *    below the fold on an unscrolled page — and it hides again the moment any
 *    part of the button re-enters the viewport, the footer scrolls in, or a
 *    modal/drawer/search/menu overlay opens. A startup/top-of-page lock keeps
 *    the bar from revealing while fonts/images are still settling before the
 *    buyer has scrolled. The per-section settings (merchant-editable in
 *    main-product.liquid) gate the bar per viewport via
 *    data-hon-sticky-desktop/mobile.
 *  - Clean pricing. Price text is read structurally from Dawn's price block
 *    (.price__regular .price-item--regular for the current price, and only
 *    when the variant is genuinely on sale the .price__sale .price-item--sale
 *    as current with .price__sale .price-item--regular as compare-at). The
 *    visually-hidden "Regular price"/"Sale price" label spans are never
 *    copied into the bar, only ONE current price is shown, and a compare-at
 *    figure appears only when it is genuinely greater than the current price.
 *  - One activation = one submit. The loading state ends only when the
 *    canonical button leaves its busy state (aria-disabled/loading cleared by
 *    Dawn's own .finally() on success AND failure), with a long fail-safe
 *    timer as a backstop only.
 *  - Sync. Button label + disabled state mirror the canonical button; the
 *    variant summary is read live from the picker; price/compare mirror the
 *    price block. Dawn's product-info re-renders by swapping nodes
 *    (variant-selects subtree and the inner of #price-<section>), so every
 *    read re-queries the live DOM and variant completion is hooked through
 *    Dawn's PUB_SUB bus (PUB_SUB_EVENTS.variantChange), never a stale node.
 *  - Choice-required safety. When the canonical button is disabled because
 *    the current variant is unavailable but an alternative is selectable,
 *    the bar never silently submits a default variant — it stays tappable
 *    with a "Choose options" label and reveals + focuses the canonical
 *    variant picker instead. A genuinely sold-out product keeps the bar
 *    disabled and mirrors "Sold out". While a variant change fetch is still
 *    in flight the bar never submits the stale hidden variant id.
 *  - Accessibility. An aria-live region announces progress and (on failure)
 *    the returned message; the spinner is visual only (aria-hidden); focus
 *    uses :focus-visible styling; duplicated summary text stays aria-hidden
 *    so screen readers never get double announcements.
 *  - Stacking coordination. While visible the script toggles
 *    html.hon-sticky-visible so CSS can reserve scroll space (an exact
 *    inline scroll-padding matches the rendered bar height) and tuck the
 *    Shopify chat launcher away; the third-party back-to-top control is
 *    lifted clear of the bar. All coordination is temporary and reversed
 *    when the bar hides.
 *
 * Init runs on DOMContentLoaded AND shopify:section:load (theme-editor /
 * Section Rendering API refresh replaces the subtree; re-running is safe
 * because each product-info node is guarded by data-hon-sticky-bound).
 */
(function () {
  var HON_STICKY_CLASS = 'hon-sticky-visible';

  function initStickyAtc(root) {
    if (root.dataset.honStickyBound === 'true') return;

    var sticky = root.querySelector('[data-hon-sticky-atc]');
    if (!sticky) return;

    var originalBtn = root.querySelector('.product-form__submit');
    if (!originalBtn) return;

    root.dataset.honStickyBound = 'true';

    var stickyBtn = sticky.querySelector('[data-hon-sticky-btn]');
    var stickyLabel = sticky.querySelector('[data-hon-sticky-label]');
    var stickyPrice = sticky.querySelector('[data-hon-sticky-price]');
    var stickyCompare = sticky.querySelector('[data-hon-sticky-compare]');
    var stickyVariant = sticky.querySelector('[data-hon-sticky-variant]');
    var stickyStatus = sticky.querySelector('[data-hon-sticky-status]');
    var chooseLabel =
      stickyBtn && stickyBtn.getAttribute('data-hon-sticky-choose')
        ? stickyBtn.getAttribute('data-hon-sticky-choose')
        : 'Choose options';

    // Merchant viewport toggles from the product section settings. Missing
    // attributes (older template) default to shown.
    var stickyDesktop = sticky.getAttribute('data-hon-sticky-desktop') !== 'false';
    var stickyMobile = sticky.getAttribute('data-hon-sticky-mobile') !== 'false';
    var desktopQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(min-width: 750px)')
        : null;

    var btnScrolledPast = false;
    // Startup/top-of-page guard: the bar may only reveal once the buyer has
    // genuinely started scrolling (see sampleButtonGeometry below). Released
    // on the first scroll event, or immediately if the browser restored a
    // mid-page scroll offset. While locked the bar can never appear, so image
    // / font layout settling at the top of the page can never leak it.
    var startupLocked = true;
    var footerVisible = false;
    var modalOpen = false;
    var submitting = false;
    var pendingSubmit = false;
    var pendingOptionChange = false;
    var failSafeTimer = null;

    function getPicker() {
      return root.querySelector('variant-selects');
    }

    function cleanText(el) {
      return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }

    function moneyToNumber(str) {
      if (!str) return NaN;
      var cleaned = str.replace(/[^0-9.,]/g, '').replace(/,/g, '');
      var n = parseFloat(cleaned);
      return isNaN(n) ? NaN : n;
    }

    // Structurally read Dawn's live price block. Only the money spans are
    // used, so the visually-hidden "Regular price"/"Sale price" labels can
    // never appear in the sticky bar, and a compare-at figure is returned
    // only when it is genuinely greater than the current price.
    function readLivePrice() {
      var rootEl = root.querySelector('.price') || root.querySelector('.price__container');
      if (!rootEl) return null;
      var saleBlock = rootEl.querySelector('.price__sale');
      var currentEl = null;
      var compareEl = null;
      if (saleBlock) {
        currentEl = saleBlock.querySelector('.price-item--sale');
        compareEl = saleBlock.querySelector('.price-item--regular');
      } else {
        currentEl = rootEl.querySelector('.price__regular .price-item--regular');
      }
      var current = cleanText(currentEl);
      var compare = cleanText(compareEl);
      if (compare && !(moneyToNumber(compare) > moneyToNumber(current))) compare = '';
      return { current: current, compare: compare };
    }

    function announceStatus(message) {
      if (!stickyStatus || !message) return;
      stickyStatus.textContent = '';
      // Force a reflow so repeating the same message still re-announces.
      void stickyStatus.offsetWidth;
      stickyStatus.textContent = message;
    }

    function clearPending() {
      submitting = false;
      pendingSubmit = false;
      stickyBtn.classList.remove('is-loading');
      if (failSafeTimer) {
        window.clearTimeout(failSafeTimer);
        failSafeTimer = null;
      }
    }

    function hasSelectableOption(picker) {
      if (!picker) return false;
      var selects = picker.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        var options = selects[s].options;
        for (var o = 0; o < options.length; o++) {
          var text = (options[o].textContent || '').toLowerCase();
          if (
            !options[o].disabled &&
            text &&
            text.indexOf('unavailable') === -1 &&
            text.indexOf('sold out') === -1
          ) {
            return true;
          }
        }
      }
      var inputs = picker.querySelectorAll('input');
      for (var i = 0; i < inputs.length; i++) {
        if (!inputs[i].disabled && !inputs[i].classList.contains('disabled')) return true;
      }
      return false;
    }

    function getOriginalLabel() {
      for (var i = 0; i < originalBtn.children.length; i++) {
        var child = originalBtn.children[i];
        if (child.tagName && child.tagName.toUpperCase() === 'SPAN') {
          var cls = child.className || '';
          if (typeof cls === 'string' && cls.indexOf('loading') !== -1) continue;
          return (child.textContent || '').replace(/\s+/g, ' ').trim();
        }
      }
      return (originalBtn.textContent || '').replace(/\s+/g, ' ').trim();
    }

    function syncVariantSummary() {
      if (!stickyVariant) return;
      var parts = [];
      var picker = getPicker();
      if (picker) {
        var selects = picker.querySelectorAll('select');
        for (var s = 0; s < selects.length; s++) {
          var chosen = selects[s].selectedOptions && selects[s].selectedOptions[0];
          var t = chosen ? (chosen.textContent || '').replace(/\s+/g, ' ').trim() : '';
          if (
            t &&
            t.toLowerCase().indexOf('unavailable') === -1 &&
            t.toLowerCase().indexOf('sold out') === -1
          ) {
            parts.push(t);
          }
        }
        var radios = picker.querySelectorAll('input[type="radio"]:checked');
        for (var r = 0; r < radios.length; r++) {
          if (radios[r].classList.contains('disabled')) continue;
          var label = radios[r].nextElementSibling;
          var lt = label ? (label.textContent || '').replace(/\s+/g, ' ').trim() : '';
          if (
            lt &&
            lt.toLowerCase().indexOf('unavailable') === -1 &&
            lt.toLowerCase().indexOf('sold out') === -1
          ) {
            parts.push(lt);
          }
        }
      }
      var summary = parts.join(' · ');
      if (stickyVariant.textContent !== summary) stickyVariant.textContent = summary;
      stickyVariant.setAttribute('aria-hidden', summary ? 'false' : 'true');
    }

    function syncFromOriginal() {
      var picker = getPicker();
      var hasChoice = !!picker && hasSelectableOption(picker);
      var busy =
        originalBtn.getAttribute('aria-disabled') === 'true' ||
        (originalBtn.classList && originalBtn.classList.contains('loading'));

      // A genuinely sold-out product (no selectable alternative) keeps the
      // bar disabled; a busy request also disables it to block double taps.
      var disabledNow = busy || (originalBtn.disabled && !hasChoice);
      stickyBtn.disabled = disabledNow;
      stickyBtn.setAttribute('aria-disabled', String(disabledNow || pendingSubmit));

      // A usable (enabled) buy button means a valid variant is present again.
      // variant:change is the normal clearing path, but fall back to this so
      // a selection that never published variant:change can't wedge the bar.
      if (pendingOptionChange && !busy && !originalBtn.disabled) {
        pendingOptionChange = false;
      }

      if (busy) {
        // Leave the current label ("Adding…" is shown via the live region).
      } else if (pendingOptionChange) {
        // Dawn's variant fetch is in flight — the canonical label/price are
        // still authoritative from the previous render, so don't flicker.
      } else if (originalBtn.disabled && hasChoice) {
        if (stickyLabel.textContent !== chooseLabel) stickyLabel.textContent = chooseLabel;
      } else {
        var label = getOriginalLabel();
        if (label && stickyLabel.textContent !== label) stickyLabel.textContent = label;
      }

      // Dawn replaces the inner of #price-<section> on variant change, so
      // always read the live node rather than holding a stale reference.
      var livePrice = readLivePrice();
      if (stickyPrice && livePrice && livePrice.current) {
        if (stickyPrice.textContent !== livePrice.current) {
          stickyPrice.textContent = livePrice.current;
        }
      }
      if (stickyCompare) {
        var compareText = livePrice ? livePrice.compare : '';
        if (stickyCompare.textContent !== compareText) stickyCompare.textContent = compareText;
        stickyCompare.hidden = !compareText;
      }

      syncVariantSummary();

      // End the busy state only when Dawn's .finally() has finished, on both
      // the success and the failure path — this is what guarantees one
      // activation can never produce a duplicate /cart/add.
      if (pendingSubmit && !busy) clearPending();
    }

    function isModalOpen() {
      // State-based only. The cart drawer's inner dialog carries aria-modal
      // permanently (visibility is CSS-driven), so matching that would keep
      // the bar permanently hidden — key off .drawer.active / [open] instead.
      if (
        document.querySelector(
          [
            'dialog[open]',
            'modal-dialog[open]',
            'quick-add-modal[open]',
            'product-modal[open]',
            '.product-popup-modal[open]',
            '.product-media-modal[open]',
            '.quick-add-modal[open]',
            '.drawer.active',
            'cart-drawer.active',
            '#CartDrawer.active',
            // Dawn toggles the header menu drawer via a native <details> that
            // gains [open] (HeaderDrawer also flags .section-header.menu-open),
            // and the header search via details-modal's inner <details>[open].
            // Scope tightly so open product collapsibles (plain <details>)
            // never count as overlays.
            '#Details-menu-drawer-container[open]',
            '.section-header.menu-open',
            '.header__search details[open]',
            'header-drawer details[open]',
            'menu-drawer details[open]',
            // Task 7 shared header-overlay convention: HonHeaderOverlay toggles
            // this single flag on <html> for both the mobile menu and the header
            // search, so the bar hides even during the transition frames before
            // the drawer's [open]/menu-open classes land.
            '.hon-header-overlay-open',
          ].join(', ')
        )
      ) {
        return true;
      }
      // HoN's custom size-guide overlay is a plain div toggled via inline
      // style.display, so detect its live display state directly.
      var honOverlay = document.querySelector('.hon-modal-overlay');
      if (honOverlay && honOverlay.style && honOverlay.style.display && honOverlay.style.display !== 'none') {
        return true;
      }
      return false;
    }

    function refreshModalState() {
      var next = isModalOpen();
      if (next !== modalOpen) {
        modalOpen = next;
        updateVisibility();
      }
    }

    // --- Third-party back-to-top coordination: lift the fixed circle above
    // the bar for as long as the bar is visible. Runtime-only, reversible,
    // no permanent hiding and no edit to the ss-* snippet. ---
    var backTopEl = null;
    var backTopLifted = false;

    function getBackTopEl() {
      if (backTopEl && backTopEl.isConnected) return backTopEl;
      var nodes = document.querySelectorAll('[class^="top-"]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].closest && nodes[i].closest('[data-hon-sticky-atc]')) continue;
        if (nodes[i].classList.contains('hon-sticky-atc')) continue;
        var style = window.getComputedStyle(nodes[i]);
        if (style.position === 'fixed' && style.zIndex < 30) {
          backTopEl = nodes[i];
          return backTopEl;
        }
      }
      return null;
    }

    function liftBackTop() {
      if (backTopLifted) return;
      var el = getBackTopEl();
      if (!el) return;
      backTopLifted = true;
      var offset = Math.ceil((sticky.getBoundingClientRect().height || 76) + 12);
      el.style.transition = 'transform 0.3s ease';
      el.style.transform = 'translateY(-' + offset + 'px)';
    }

    function lowerBackTop() {
      if (!backTopLifted) return;
      backTopLifted = false;
      if (backTopEl && backTopEl.isConnected) {
        backTopEl.style.transform = '';
        backTopEl.style.transition = '';
      }
    }

    function viewportAllowed() {
      if (!desktopQuery) return true;
      return desktopQuery.matches ? stickyDesktop : stickyMobile;
    }

    function setBodyState(active) {
      var html = document.documentElement;
      if (active) {
        html.classList.add(HON_STICKY_CLASS);
        var h = Math.ceil(sticky.getBoundingClientRect().height || 0);
        if (h > 0) html.style.scrollPaddingBottom = h + 'px';
      } else {
        html.classList.remove(HON_STICKY_CLASS);
        html.style.scrollPaddingBottom = '';
      }
    }

    function updateVisibility() {
      var shouldShow =
        btnScrolledPast && !footerVisible && !modalOpen && viewportAllowed();
      sticky.hidden = !shouldShow;
      setBodyState(shouldShow);
      if (shouldShow) liftBackTop();
      else lowerBackTop();
    }

    // --- Visibility: directional geometry of the canonical buy control ---
    // The bar may show ONLY once the real .product-form__submit button has
    // been moved above the top of the viewport by scroll. Raw intersection is
    // not enough: a button still below the fold on the unscrolled page (rect.top
    // >= window.innerHeight) must NOT count as "bypassed" — that is exactly what
    // made the old !entry.isIntersecting test leak the bar at y=0. We read
    // getBoundingClientRect() and treat the control as bypassed only when its
    // whole box sits above the top edge (rect.bottom <= 0).
    var geometryRaf = null;

    function sampleButtonGeometry() {
      geometryRaf = null;
      if (!originalBtn.isConnected) return;
      var rect = originalBtn.getBoundingClientRect();
      // Directional, never intersection-only:
      //  - rect.bottom <= 0               -> whole control ABOVE the viewport
      //                                     top: buyer has scrolled past it.
      //  - rect.top >= window.innerHeight -> control still BELOW the fold
      //                                     (page-top state): not bypassed.
      //  - anything in between (partial)  -> control usable: not bypassed.
      btnScrolledPast = rect.bottom <= 0;
      updateVisibility();
    }

    // The bar is hidden until the buyer has actually scrolled: geometry is
    // only sampled after the startup lock is released, so an idle startup
    // measurement taken while fonts/images are still settling can never reveal
    // the bar before the canonical purchase control has been bypassed.
    function scheduleGeometrySample() {
      if (startupLocked) {
        // A restored mid-page scroll offset (browser back / refresh) counts as
        // real scroll input; an untouched page at y=0 stays locked.
        if (window.scrollY <= 0) return;
        startupLocked = false;
      }
      if (!geometryRaf) geometryRaf = window.requestAnimationFrame(sampleButtonGeometry);
    }

    window.addEventListener(
      'scroll',
      function () {
        startupLocked = false;
        if (!geometryRaf) geometryRaf = window.requestAnimationFrame(sampleButtonGeometry);
      },
      { passive: true }
    );
    // Take one guarded sample at startup: honours the lock at the top of the
    // page, and reveals correctly if the browser restored a mid-page scroll.
    scheduleGeometrySample();

    // Layout-settle safety: samples are normally driven by scroll/resize, but
    // content above the buy control (images, header, media gallery) can settle
    // and push the button above the viewport top with no scroll event in
    // between, which would otherwise leave the bar hidden while the canonical
    // control is fully bypassed. Watch the control itself so any change in its
    // viewport intersection re-runs the same guarded sample. The IO event is
    // only a trigger: reveal still requires rect.bottom <= 0 (directional) and
    // still honours the startup lock, so a button below the fold can never
    // leak the bar.
    if ('IntersectionObserver' in window) {
      var btnObserver = new IntersectionObserver(function () {
        scheduleGeometrySample();
      });
      btnObserver.observe(originalBtn);
    }

    // Footer gate (unchanged behaviour): the bar hides as the footer scrolls
    // into view.
    if ('IntersectionObserver' in window) {
      var footerEl = document.querySelector('footer, .footer');
      if (footerEl) {
        var footerObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              footerVisible = entry.isIntersecting;
              updateVisibility();
            });
          },
          { threshold: 0 }
        );
        footerObserver.observe(footerEl);
      }
    }

    // --- Modal/drawer/dialog state: MutationObserver + native events only ---
    var bodyModalObserver = new MutationObserver(refreshModalState);
    bodyModalObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['open', 'class', 'aria-modal', 'hidden'],
      subtree: true,
    });
    document.addEventListener('cart:open', refreshModalState);
    document.addEventListener('cart:close', refreshModalState);
    // Tab 7 header overlays dispatch these explicitly so the sticky bar reacts
    // immediately rather than waiting on the body-class MutationObserver.
    document.addEventListener('hon:header-overlay-open', refreshModalState);
    document.addEventListener('hon:header-overlay-close', refreshModalState);
    refreshModalState();

    // --- Sync: buy-button busy/label/disabled + price block updates.
    // Dawn keeps the submit button node and toggles it (disabled, aria-disabled,
    // label text) — an attribute/characterData observer on it is therefore
    // stable across variant changes. The price block is re-rendered by
    // replacing the inner of the persistent #price-<section> wrapper, so we
    // observe that wrapper and re-read live on every mutation. ---
    var syncObserver = new MutationObserver(syncFromOriginal);
    syncObserver.observe(originalBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    var priceWrapper = root.querySelector('[id^="price-"]');
    if (priceWrapper && 'MutationObserver' in window) {
      var priceObserver = new MutationObserver(syncFromOriginal);
      priceObserver.observe(priceWrapper, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // User picker interactions (delegated so it survives the variant-selects
    // subtree swap) and Dawn's PUB_SUB bus for the completed variant change.
    root.addEventListener('change', function () {
      if (root.isConnected) syncFromOriginal();
    });

    var pubSubBound = false;
    function bindPubSub() {
      if (pubSubBound) return;
      if (typeof subscribe !== 'function') return;
      if (typeof PUB_SUB_EVENTS === 'undefined') return;
      pubSubBound = true;

      // Fires the moment the buyer picks an option (fetch still in flight).
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function (payload) {
        var data = payload && payload.data;
        if (!data || !data.event) return;
        if (!root.isConnected || !root.contains(data.event.target)) return;
        pendingOptionChange = true;
      });

      // Fires after Dawn has swapped variant-selects/price and toggled the
      // submit button — the authoritative moment to re-sync.
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        if (!root.isConnected) {
          // A cross-product swap replaced this product-info entirely;
          // rebind the fresh subtree (guarded by data-hon-sticky-bound).
          window.setTimeout(init, 0);
          return;
        }
        pendingOptionChange = false;
        syncFromOriginal();
      });
    }
    bindPubSub();

    // --- Failure announcement: canonical Dawn reveals its error wrapper ---
    var errorWrapper = root.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper && 'MutationObserver' in window) {
      var errorObserver = new MutationObserver(function () {
        if (!pendingSubmit) return;
        var wrapper = root.querySelector('.product-form__error-message-wrapper');
        if (!wrapper || wrapper.hasAttribute('hidden')) return;
        var msgEl = wrapper.querySelector('.product-form__error-message');
        var msg = msgEl ? (msgEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
        announceStatus(msg || 'Could not add to your bag');
        // The request has already resolved with an error — safe to re-arm.
        clearPending();
      });
      errorObserver.observe(errorWrapper, { attributes: true, attributeFilter: ['hidden'] });
    }

    window.addEventListener('resize', scheduleGeometrySample);
    window.addEventListener('orientationchange', scheduleGeometrySample);
    if (desktopQuery) {
      if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', scheduleGeometrySample);
      else if (desktopQuery.addListener) desktopQuery.addListener(scheduleGeometrySample);
    }

    // --- Choice-required: reveal + focus the canonical option selector ---
    function revealOptionChoice() {
      var picker = getPicker();
      if (!picker) return;
      var target = null;
      var selects = picker.querySelectorAll('select');
      for (var s = 0; s < selects.length && !target; s++) {
        var options = selects[s].options;
        for (var o = 0; o < options.length; o++) {
          var text = (options[o].textContent || '').toLowerCase();
          if (
            !options[o].disabled &&
            text &&
            text.indexOf('unavailable') === -1 &&
            text.indexOf('sold out') === -1
          ) {
            target = selects[s];
            break;
          }
        }
      }
      if (!target) {
        var inputs = picker.querySelectorAll('input');
        for (var i = 0; i < inputs.length; i++) {
          if (!inputs[i].disabled && !inputs[i].classList.contains('disabled')) {
            target = inputs[i];
            break;
          }
        }
      }
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(function () {
          target.focus({ preventScroll: true });
        }, 450);
      }
    }

    // --- Trigger: safely click the real button, never our own cart logic ---
    stickyBtn.addEventListener('click', function (event) {
      event.preventDefault();
      if (submitting) return;

      if (originalBtn.disabled && !hasSelectableOption(getPicker())) return;

      // Never submit a stale hidden variant id while Dawn's variant-change
      // fetch is unresolved — surface the picker for a fresh selection.
      if (pendingOptionChange || originalBtn.disabled) {
        revealOptionChoice();
        return;
      }

      submitting = true;
      pendingSubmit = true;
      stickyBtn.classList.add('is-loading');
      announceStatus('Adding to your bag');
      window.clearTimeout(failSafeTimer);
      failSafeTimer = window.setTimeout(function () {
        if (pendingSubmit) clearPending();
      }, 10000);
      originalBtn.click();
    });

    syncFromOriginal();
    updateVisibility();
  }

  function init() {
    var productInfos = document.querySelectorAll('product-info');
    for (var i = 0; i < productInfos.length; i++) {
      initStickyAtc(productInfos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
